import React, { useEffect, useState } from 'react';
import { bannersAPI, settingsAPI } from '../utils/api';
import HScroller from './HScroller';

/**
 * iter135 — Shared helper used by BOTH the strip and hero branches to
 * append `?ad_view=public` when a banner deep-links to an internal
 * /listing/... route. This ensures the seller viewing their own
 * banner-linked ad ALSO sees the current "Ad Sold" / "Ad Expired" state
 * (matching mobile-anonymous behaviour). Iter123's owner-bypass otherwise
 * makes desktop owners see the ad as live after they marked it Sold.
 * Extracted into a single helper so the strip + hero paths cannot diverge
 * again (iter134 accidentally applied it to only the strip branch).
 */
export const resolveBannerHref = (linkUrl) => {
  if (!linkUrl) return linkUrl;
  let href = linkUrl;
  try {
    const isExternal = href.startsWith('http');
    const path = isExternal ? new URL(href).pathname : href;
    if (path && path.startsWith('/listing/')) {
      const sep = href.includes('?') ? '&' : '?';
      href = `${href}${sep}ad_view=public`;
    }
  } catch (_) { /* keep original href */ }
  return href;
};

/**
 * Renders active admin-uploaded banners for a given homepage position.
 * - position="homepage_hero": full-width hero carousel (wraps Link if link_url present)
 * - position="homepage_strip": narrow promo strip (multiple banners side-by-side)
 */
const HomeBanners = ({ position = 'homepage_hero' }) => {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    let cancelled = false;
    bannersAPI.list(position)
      .then((res) => { if (!cancelled) setBanners(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setBanners([]); });
    return () => { cancelled = true; };
  }, [position]);

  if (!banners.length) return null;

  if (position === 'homepage_strip') {
    return (
      <div className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 py-3" data-testid={`banner-strip-${position}`}>
        <HScroller ariaLabel="promo banners" testId={`banner-strip-${position}-scroller`} autoSlide>
          <div className="flex gap-3 pb-1 px-1 md:px-10">
            {banners.map((b) => {
              const inner = (
                <div className="flex-shrink-0 w-72 sm:w-80 rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`banner-${b.banner_id}`}>
                  <img src={b.image_url} alt={b.title} className="w-full h-24 object-cover" />
                  {(b.title || b.subtitle) && (
                    <div className="p-2 bg-white">
                      {b.title && <p className="text-xs font-bold text-[#0A0B10] truncate">{b.title}</p>}
                      {b.subtitle && <p className="text-[10px] text-[#6B7280] truncate">{b.subtitle}</p>}
                    </div>
                  )}
                </div>
              );
              return b.link_url
                ? (() => {
                    const href = resolveBannerHref(b.link_url);
                    const targetExternal = href.startsWith('http');
                    return <a key={b.banner_id} href={href} target={targetExternal ? '_blank' : '_self'} rel="noopener noreferrer">{inner}</a>;
                  })()
                : <div key={b.banner_id}>{inner}</div>;
            })}
          </div>
        </HScroller>
      </div>
    );
  }

  // homepage_hero — iter63 — auto-sliding carousel across ALL admin-uploaded banners.
  // Slides at the admin-configured interval (default 3s), pauses on hover (desktop),
  // supports any banner count. The interval is fetched once from /api/settings.
  return <HeroCarousel banners={banners} />;
};

const HeroCarousel = ({ banners }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Interval in ms — populated from /api/settings.hero_autoplay_seconds. We
  // default to 3000 so nothing flickers if the settings call is slow.
  const [intervalMs, setIntervalMs] = useState(3000);

  useEffect(() => {
    let cancelled = false;
    settingsAPI.get()
      .then((res) => {
        if (cancelled) return;
        const secs = Number(res?.data?.hero_autoplay_seconds);
        if (Number.isFinite(secs) && secs >= 2 && secs <= 15) {
          setIntervalMs(secs * 1000);
        }
      })
      .catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || banners.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % banners.length), intervalMs);
    return () => clearInterval(id);
  }, [paused, banners.length, intervalMs]);

  // Clamp index when banners change (e.g. admin deletes the currently-shown banner)
  useEffect(() => {
    if (idx >= banners.length) setIdx(0);
  }, [banners.length, idx]);

  if (!banners.length) return null;

  return (
    <div
      className="max-w-[1440px] mx-auto px-3 sm:px-8 lg:px-12 py-3"
      data-testid="banner-hero-homepage_hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* iter95 — Entire card slides as one unit.
          - Aspect ratio uses aspect-[16/6] on mobile (fixes iPhone bottom crop
            that iter89's 16/5 caused) and reverts to 16/5 on sm+ so tablets/
            desktops stay unchanged.
          - Each slide has its own rounded corners + shadow (slides as ONE
            complete card, not just the image).
          - `gap-3` on the flex track creates a small WHITE gap between
            adjacent banners during the transition. Because gap width is
            counted in the flex layout, we compensate by translating the
            track slightly more than 100% per slide using a CSS custom
            property below. */}
      <div className="relative w-full aspect-[16/6] sm:aspect-[16/5] min-h-[160px] sm:min-h-[180px] overflow-hidden" data-testid="hero-carousel">
        <div
          className="flex h-full w-full gap-3 transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(calc(-${idx * 100}% - ${idx * 0.75}rem))` }}
          data-testid="hero-slide-track"
        >
          {banners.map((b) => {
            const inner = (
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-lg" data-testid={`banner-${b.banner_id}`}>
                <img src={b.image_url} alt={b.title || 'banner'} className="w-full h-full object-cover" />
                {/* iter139 — Dark gradient overlay REMOVED so uploaded images
                     render as-is (full brightness/sharpness). Title block
                     keeps its exact position/typography but now respects the
                     per-banner Show/Hide Title setting (show_title). */}
                {b.show_title !== false && (b.title || b.subtitle) && (
                  <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6">
                    {b.title && <h3 className="text-white text-lg sm:text-2xl font-bold drop-shadow">{b.title}</h3>}
                    {b.subtitle && <p className="text-white/90 text-xs sm:text-sm mt-1 drop-shadow">{b.subtitle}</p>}
                  </div>
                )}
              </div>
            );
            return b.link_url ? (
              <a
                key={b.banner_id}
                href={resolveBannerHref(b.link_url)}
                target={b.link_url.startsWith('http') ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="relative flex-shrink-0 w-full h-full"
              >
                {inner}
              </a>
            ) : (
              <div key={b.banner_id} className="relative flex-shrink-0 w-full h-full">{inner}</div>
            );
          })}
        </div>
        {/* Dots — quick visual indicator + manual navigation */}
        {banners.length > 1 && (
          <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid="hero-dots">
            {banners.map((b, i) => (
              <button
                key={b.banner_id}
                type="button"
                data-testid={`hero-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to banner ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'bg-white w-5' : 'bg-white/50 w-1.5'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeBanners;
